// src/components/ui/TagGuide.js
//
// 5-page guide for the Tags feature. Unlike BulkTagGuide (which stays anchored
// to a toolbar), this guide moves across the screen on each step, pointing at:
//   1. The icon button that summoned it (triggerEl prop)
//   2. The "create tag" button in the selected CaseListItem
//   3. The tags cell of the first document in the PDF table
//   4. The first tag pill in the sidebar (or a fake "Trial" tag)
//   5. The "select all" checkbox in the PDF table header
//
// Each step: the panel repositions near its target (above or below, best fit)
// and the arrow points at the center of the target element.
//
// Hover surfacing on steps 2–5 is handled by GuideForceContext consumers —
// this component just positions itself and renders.

import React, { useState, useLayoutEffect, useEffect, useRef, useCallback } from 'react';
import GuidePanel from './GuidePanel';

const STEPS = [
  {
    title: '',      // Just "Tags" (bold, no colon — see GuidePanel empty-title handling)
    body: 'Tags let you export specific groups of documents from a case. Create a tag, add it to docs, and select it when exporting.',
    nextLabel: 'Learn More',
    target: null,   // Uses triggerEl prop
  },
  {
    title: 'Create',
    body: 'Create tags for your case here.',
    target: 'tag-guide-create-tag',
  },
  {
    title: 'Add to documents',
    body: 'Add or remove tags from documents here.',
    target: 'tag-guide-first-tags-cell',
  },
  {
    title: 'Select to filter',
    body: (
      <>
        Select the tag to filter to the group of documents you want to export.{' '}
        <span className="italic text-app-accent-400">Each doc's exhibit number will always stay the same.</span>
      </>
    ),
    nextLabel: 'Bonus',
    target: 'tag-guide-first-tag',
    placement: 'rightOf',  // guide to the right of target; arrow points left
  },
  {
    title: 'Bulk actions',
    body: 'Select multiple documents to add or remove tags from all of them.',
    target: 'tag-guide-select-all',
    placement: 'leftOf',   // guide to the left of target; arrow points right
  },
];

const GUIDE_WIDTH = 248;
const GUIDE_GAP = 12;   // Gap between target element and guide edge

const ARROW_SIZE = 12;
const ARROW_HALF = ARROW_SIZE / 2;       // 6
const ARROW_EDGE_INSET = ARROW_HALF - 2; // 4 → tip protrudes ~6.5 px past panel edge

/**
 * @param {boolean}        visible
 * @param {1|2|3|4|5}      step
 * @param {Element|null}   triggerEl  DOM element of the button that opened the guide (step 1 target)
 * @param {function}       onNext
 * @param {function}       onGotIt
 * @param {function}       onClose
 * @param {function}       onStepChange
 */
const TagGuide = ({ visible, step, triggerEl, onNext, onGotIt, onClose, onStepChange }) => {
  const guideRef = useRef(null);
  // pos.side: 'top' (arrow at top, guide below) | 'bottom' (arrow at bottom, guide above)
  //           'left' (arrow at left, guide right of target) | 'right' (arrow at right, guide left of target)
  const [pos, setPos] = useState(null);
  const [arrowLeft, setArrowLeft] = useState(GUIDE_WIDTH / 2);  // for top/bottom arrows
  const [arrowTop, setArrowTop] = useState(80);                 // for left/right arrows
  const [isClosing, setIsClosing] = useState(false);
  const [closingOrigin, setClosingOrigin] = useState('center center');
  const [mounted, setMounted] = useState(false);

  const recalc = useCallback(() => {
    // Resolve target element for this step
    let targetEl = null;
    if (step === 1) {
      targetEl = triggerEl;
    } else {
      const targetKey = STEPS[step - 1]?.target;
      targetEl = targetKey
        ? document.querySelector(`[data-guide-target="${targetKey}"]`)
        : null;
    }
    if (!targetEl) return;

    const rect = targetEl.getBoundingClientRect();
    const guideH = guideRef.current?.offsetHeight ?? 160;
    const placement = STEPS[step - 1]?.placement;

    if (placement === 'rightOf') {
      // Guide to the right of the target; arrow at left edge pointing left
      const left = Math.min(rect.right + GUIDE_GAP, window.innerWidth - GUIDE_WIDTH - 8);
      const idealTop = rect.top + rect.height / 2 - guideH / 2;
      const top = Math.max(8, Math.min(idealTop, window.innerHeight - guideH - 8));
      const at = Math.max(14, Math.min(rect.top + rect.height / 2 - top, guideH - 14));
      setPos({ left, top, side: 'left' });
      setArrowTop(at);
      return;
    }

    if (placement === 'leftOf') {
      // Guide to the left of the target; arrow at right edge pointing right
      const left = Math.max(8, rect.left - GUIDE_GAP - GUIDE_WIDTH);
      const idealTop = rect.top + rect.height / 2 - guideH / 2;
      const top = Math.max(8, Math.min(idealTop, window.innerHeight - guideH - 8));
      const at = Math.max(14, Math.min(rect.top + rect.height / 2 - top, guideH - 14));
      setPos({ left, top, side: 'right' });
      setArrowTop(at);
      return;
    }

    // Default: above/below placement
    const spaceBelow = window.innerHeight - rect.bottom - GUIDE_GAP;
    const spaceAbove = rect.top - GUIDE_GAP;
    const showBelow = spaceBelow >= guideH || spaceBelow >= spaceAbove;

    let left = rect.left + rect.width / 2 - GUIDE_WIDTH / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - GUIDE_WIDTH - 8));

    const top = showBelow
      ? rect.bottom + GUIDE_GAP
      : rect.top - guideH - GUIDE_GAP;

    const al = Math.max(14, Math.min(
      rect.left + rect.width / 2 - left,
      GUIDE_WIDTH - 14
    ));

    setPos({ left, top, side: showBelow ? 'top' : 'bottom' });
    setArrowLeft(al);
  }, [step, triggerEl]);

  // Reposition whenever visible, step, or triggerEl changes
  useLayoutEffect(() => {
    if (!visible) {
      setMounted(false);
      return;
    }
    // Give hover-surfacing effects a frame to render before measuring
    const t = setTimeout(() => {
      recalc();
      requestAnimationFrame(() => setMounted(true));
    }, 40);
    return () => clearTimeout(t);
  }, [visible, step, triggerEl, recalc]);

  useEffect(() => {
    if (!visible) return;
    window.addEventListener('resize', recalc);

    const layoutContainer = document.querySelector('[data-guide-layout]');
    let ro;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(recalc);
      if (layoutContainer) ro.observe(layoutContainer);
      // Step 1's target can be the export dropdown's content panel, which resizes
      // when the "export by tag" pane expands/collapses — reposition when it does.
      if (step === 1 && triggerEl instanceof Element) ro.observe(triggerEl);
    }

    return () => {
      window.removeEventListener('resize', recalc);
      ro?.disconnect();
    };
  }, [visible, step, triggerEl, recalc]);

  const animateClose = useCallback((callback) => {
    // Collapse toward the triggering element if available
    if (triggerEl && guideRef.current) {
      const tr = triggerEl.getBoundingClientRect();
      const gr = guideRef.current.getBoundingClientRect();
      setClosingOrigin(
        `${tr.left + tr.width / 2 - gr.left}px ${tr.top + tr.height / 2 - gr.top}px`
      );
    } else {
      setClosingOrigin('center center');
    }
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setMounted(false);
      callback?.();
    }, 220);
  }, [triggerEl]);

  const handleClose = useCallback(() => animateClose(onClose), [animateClose, onClose]);
  const handleGotIt = useCallback(() => animateClose(onGotIt), [animateClose, onGotIt]);

  if (!visible && !isClosing) return null;
  if (!pos) return null;

  return (
    <GuidePanel
      ref={guideRef}
      steps={STEPS}
      step={step}
      titlePrefix="Tags"
      onNext={onNext}
      onGotIt={handleGotIt}
      onClose={handleClose}
      onStepChange={onStepChange}
      style={{
        left: pos.left,
        top: pos.top,
        transformOrigin: isClosing ? closingOrigin : 'center center',
        transform: isClosing ? 'scale(0.05)' : mounted ? 'scale(1)' : 'scale(0.92)',
        opacity: isClosing ? 0 : mounted ? 1 : 0,
        // Smooth panel movement between steps
        transition: isClosing
          ? 'transform 220ms cubic-bezier(0.4, 0, 1, 1), opacity 180ms ease-in'
          : mounted
            ? 'transform 180ms ease-out, opacity 150ms ease-out, left 220ms ease, top 220ms ease'
            : 'transform 180ms ease-out, opacity 150ms ease-out',
      }}
    >
      {/*
        Arrow — rotated square matching PopoverWrapper dark theme.
        side: 'top'   → arrow at top edge    (guide is below target)
        side: 'bottom'→ arrow at bottom edge (guide is above target)
        side: 'left'  → arrow at left edge   (guide is right of target)
        side: 'right' → arrow at right edge  (guide is left of target)
      */}
      <div
        className="absolute bg-app-gray-800 rounded-[2px] pointer-events-none"
        style={{
          width: ARROW_SIZE,
          height: ARROW_SIZE,
          transform: 'rotate(45deg)',
          ...(pos.side === 'top' && {
            left: arrowLeft,
            marginLeft: -ARROW_HALF,
            top: -ARROW_EDGE_INSET,
            bottom: 'auto',
            transition: mounted ? 'left 200ms ease' : 'none',
          }),
          ...(pos.side === 'bottom' && {
            left: arrowLeft,
            marginLeft: -ARROW_HALF,
            bottom: -ARROW_EDGE_INSET,
            top: 'auto',
            transition: mounted ? 'left 200ms ease' : 'none',
          }),
          ...(pos.side === 'left' && {
            left: -ARROW_EDGE_INSET,
            right: 'auto',
            top: arrowTop,
            marginTop: -ARROW_HALF,
          }),
          ...(pos.side === 'right' && {
            right: -ARROW_EDGE_INSET,
            left: 'auto',
            top: arrowTop,
            marginTop: -ARROW_HALF,
          }),
        }}
      />
    </GuidePanel>
  );
};

export default TagGuide;